import { translate } from "app/language/service"
import { MAX_FIELD_LENGTH, NOT_SAVED_ID } from "app/model"
import { getStore } from "app/store/store"
import { deepEquals, isEmptyObject } from "app/tools/common"
import { getAllWeekDaysOfPeriod, getDifferenceInDay } from "app/util/date/tools/common"
import { ALL_AUCTION_TYPES, CAMPAIGN_AUCTION_TYPES, SSP_DEAL_AUCTION_TYPES } from "auction/model"
import {
  CAMPAIGN_STATUSES,
  DEFAULT_CAMPAIGN_LANGUAGE,
  ICampaign,
  ICampaignUpdateParams,
  SAVE_TIMEOUT_IN_MS
} from "campaign/model"
import { campaignRPO } from "campaign/repository"
import { isStatusRunningOrRunned } from "campaign/tools/common"
import { validateDuplicateFormData } from "campaign/tools/duplicate"
import { ICap } from "cap/model"
import { CURRENCIES } from "currency/model"
import { convertValueIntoCurrency, getAvailableCurrencyIds } from "currency/tools/common"
import { IDspDeal, IUpdateDspDealParams } from "dsp-deal/model"
import { createDspDeal, updateDspDeal } from "dsp-deal/tools/common"
import { refreshInventory } from "form/inventory/tools/common"
import {
  CONFIGURATION_CHANGE_LABEL_KEY,
  DEFAULT_DSP_DEAL_AUCTION_TYPE,
  DEFAULT_FORM_CURRENCY,
  FORM_SAVE_ERRORS,
  IForm,
  IFormChange,
  IFormConfiguration,
  IFormConfigurationBudget,
  IFormConfigurationCaps,
  IFormConfigurationDspDeal,
  IFormConfigurationMain,
  IFormConfigurationModifiedData,
  IFormConfigurationSchedule
} from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getFormData } from "form/store/state"
import { synchronizeAuctions } from "form/tools/auction"
import { getCapsInterdependentErrors, isValidCap, synchronizeCaps } from "form/tools/cap"
import { synchronizeSchedule, synchronizeWeather } from "form/tools/schedule"
import { addAllSspToNewCampaign } from "form/tools/ssp"
import { FormikErrors } from "formik"
import { IOrder } from "order/model"
import { getForecastOrder } from "order/tools/campaign-forecast"
import { ALL_WEATHERS } from "schedule/model"
import { getUserAgency } from "user/tools/common"
import { addAuctionsToNewCampaign } from "./auction"
import { finalizeChange, getEmptyFormChange, getFinalizedArrayChange } from "./change"
import { synchronizeDspDealAdvertiserDomains } from "./dsp-deal"
import { synchronizeUserTags } from "./tags"

export function createOrUpdateCampaign(form: IForm): void {
  clearTimeout(state.saveFormTimeout)
  if (
    !form.context.configuration.isValid ||
    !form.context.screens.isValid ||
    !form.context.geoTargets.isValid ||
    !form.context.creatives.isValid
  ) {
    getStore().dispatch(formActionCreator.setSaveError(form.context.appContext, FORM_SAVE_ERRORS.INVALID_FORM))
    return
  }
  if (form.context.isSavingTempCampaign || state.pendingFormToSave) {
    state.pendingFormToSave = form
    return
  }
  getStore().dispatch(formActionCreator.startSavingTempCampaign(form.context.appContext))
  state.saveFormTimeout = setTimeout(() => processCreateOrUpdateCampaign(form), SAVE_TIMEOUT_IN_MS)
}

export function getConfigurationUpdateParams(
  form: IForm,
  modifiedData: IFormConfigurationModifiedData // here must pass change on lastSavedData
): ICampaignUpdateParams {
  const params: ICampaignUpdateParams = { id: form.tempCampaignId }
  // MAIN
  if (typeof modifiedData.main.name === "string") {
    params.product = modifiedData.main.name as string
  }
  if (modifiedData.main.hasOwnProperty("description")) {
    params.description = modifiedData.main.description || ""
  }
  if (typeof modifiedData.main.order_id === "number") {
    params.order_id = modifiedData.main.order_id as number
  }
  if (typeof modifiedData.main.advertiser_id === "number") {
    params.advertiser_id = modifiedData.main.advertiser_id as number
  }
  if (modifiedData.main.hasOwnProperty("max_views")) {
    params.max_views = modifiedData.main.max_views
  }
  if (typeof modifiedData.main.unlimitedImpressions === "boolean" && modifiedData.main.unlimitedImpressions) {
    params.max_views = 0
  }
  if (modifiedData.main.startDate) {
    params.start_date = modifiedData.main.startDate.toISOString()
  }
  if (modifiedData.main.endDate) {
    params.end_date = modifiedData.main.endDate.toISOString()
  }
  params.language_id = form.configuration.main.language_id as any
  if (modifiedData.main.hasOwnProperty("isBiddingOnlyOnDeals")) {
    params.is_bidding_only_on_deals = modifiedData.main.isBiddingOnlyOnDeals
  }
  // BUDGET
  if (modifiedData.budget.currency_id) {
    params.currency_id = modifiedData.budget.currency_id as any
  }
  if (modifiedData.budget.hasOwnProperty("budget")) {
    params.budget = modifiedData.budget.budget || null
  }
  if (modifiedData.budget.hasOwnProperty("max_bid")) {
    params.rate = modifiedData.budget.max_bid || null
  }
  if (modifiedData.budget.hasOwnProperty("equalizeDailyMaxSpendFromSchedule")) {
    params.equalizing_filter = modifiedData.budget.equalizeDailyMaxSpendFromSchedule
  }
  if (modifiedData.budget.hasOwnProperty("max_daily_spend")) {
    params.max_daily_spend = modifiedData.budget.max_daily_spend
  }
  // SCHEDULE
  if (modifiedData.schedule.hasOwnProperty("min_celsius")) {
    params.min_celsius = modifiedData.schedule.min_celsius || null
  }
  if (modifiedData.schedule.hasOwnProperty("max_celsius")) {
    params.max_celsius = modifiedData.schedule.max_celsius || null
  }
  return params
}

export function getConfigurationDspDealUpdateParams(
  form: IForm,
  modifiedData: IFormConfigurationModifiedData // here must pass change on lastSavedData
): IUpdateDspDealParams {
  const params: IUpdateDspDealParams = { id: form.tempDspDealId }
  // MAIN
  if (typeof modifiedData.dspDeal.dealid === "string") {
    params.dealid = modifiedData.dspDeal.dealid as string
  }
  if (typeof modifiedData.dspDeal.description === "string") {
    params.description = modifiedData.dspDeal.description || ""
  }
  if (typeof modifiedData.dspDeal.bid_floor === "number") {
    params.bid_floor = modifiedData.dspDeal.bid_floor as number
  }
  if (typeof modifiedData.dspDeal.auction_type === "number") {
    params.auction_type = modifiedData.dspDeal.auction_type as number
  }
  if (typeof modifiedData.dspDeal.dsp_id === "number") {
    params.dsp_id = modifiedData.dspDeal.dsp_id as number
  }
  return params
}

export async function updateConfigurationRelations(
  form: IForm,
  modifiedData: IFormConfigurationModifiedData
): Promise<void> {
  const promises: Array<Promise<any>> = []
  if (modifiedData.budget.auctions) {
    promises.push(synchronizeAuctions(form).catch(error => console.error("Error saving auction relations", error)))
  }
  if (modifiedData.schedule.scheduleItems || typeof modifiedData.schedule.noSchedule === "boolean") {
    promises.push(synchronizeSchedule(form).catch(error => console.error("Error saving schedule relations", error)))
  }
  if (modifiedData.schedule.weathers) {
    promises.push(synchronizeWeather(form).catch(error => console.error("Error saving weather relations", error)))
  }
  if (modifiedData.caps) {
    promises.push(synchronizeCaps(form).catch(error => console.error("Error saving caps relations", error)))
  }
  if (modifiedData.main.userTagIds) {
    promises.push(synchronizeUserTags(form).catch(error => console.error("Error saving userTag relations", error)))
  }
  if (modifiedData.dspDeal.advertiserDomainIds) {
    promises.push(
      synchronizeDspDealAdvertiserDomains(form).catch(error =>
        console.error("Error saving dsp deal advertiser domain relations", error)
      )
    )
  }
  try {
    await Promise.allSettled(promises)
  } catch (error) {
    // we should never pass here
    console.error(error)
  }
}

export function onConfigurationFormChange(
  form: IForm,
  configurationForm: IFormConfiguration
): FormikErrors<IFormConfiguration> {
  const configurationErrors: FormikErrors<IFormConfiguration> = {
    main: validateConfigurationMain(form.context.isForecast, form.context.isDspDeal, configurationForm.main),
    budget: validateConfigurationBudget(form.context.isDspDeal, configurationForm, configurationForm.budget),
    schedule: validateConfigurationSchedule(configurationForm.main, configurationForm.schedule),
    caps: validateConfigurationCaps(configurationForm, configurationForm.caps),
    dspDeal: form.context.isDspDeal ? validateConfigurationDspDeal(configurationForm, configurationForm.dspDeal) : {}
  }
  const newForm: IForm = {
    ...form,
    context: {
      ...form.context,
      configuration: {
        isValid:
          JSON.stringify(configurationErrors.main) === "{}" &&
          JSON.stringify(configurationErrors.budget) === "{}" &&
          JSON.stringify(configurationErrors.schedule) === "{}" &&
          JSON.stringify(configurationErrors.caps) === "{}" &&
          JSON.stringify(configurationErrors.dspDeal) === "{}",
        errors: configurationErrors
      }
    },
    configuration: configurationForm
  }
  newForm.changes.configuration = getConfigurationChanges(newForm)
  getStore().dispatch(
    formActionCreator.setForm({
      form: newForm,
      changes: {
        configuration: getConfigurationChanges(newForm)
      }
    })
  )
  createOrUpdateCampaign(newForm) // delayed process, done after 1 seconde of inactivity
  return configurationErrors
}

export function getConfigurationChanges(form: IForm): IFormChange[] {
  const modifiedData = getModifiedDataSinceLastUpdate(form, true)
  const campaignChange = getEmptyFormChange(CONFIGURATION_CHANGE_LABEL_KEY)
  if (modifiedData.main.hasOwnProperty("name")) {
    campaignChange.updatedItems.push(translate("label.name"))
  }
  if (modifiedData.main.hasOwnProperty("description")) {
    campaignChange.updatedItems.push(translate("label.description"))
  }
  if (modifiedData.main.hasOwnProperty("order_id")) {
    campaignChange.updatedItems.push(translate("label.order"))
  }
  if (modifiedData.main.hasOwnProperty("advertiser_id")) {
    campaignChange.updatedItems.push(translate("label.advertiser"))
  }
  if (modifiedData.main.hasOwnProperty("language_id")) {
    campaignChange.updatedItems.push(translate("label.language"))
  }
  if (modifiedData.main.hasOwnProperty("startDate")) {
    campaignChange.updatedItems.push(translate("label.startDate"))
  }
  if (modifiedData.main.hasOwnProperty("endDate")) {
    campaignChange.updatedItems.push(translate("label.endDate"))
  }
  if (modifiedData.main.hasOwnProperty("unlimitedImpressions")) {
    campaignChange.updatedItems.push(translate("label.impressions.unlimited"))
  } else if (!form.configuration.main.unlimitedImpressions && modifiedData.main.hasOwnProperty("max_views")) {
    campaignChange.updatedItems.push(translate("label.impressions"))
  }
  if (modifiedData.main.hasOwnProperty("isBiddingOnlyOnDeals")) {
    campaignChange.updatedItems.push(translate("campaign.ssp-deal.bid.only.on.deal"))
  }
  // BUDGET
  if (modifiedData.budget.hasOwnProperty("currency_id")) {
    campaignChange.updatedItems.push(translate("label.currency"))
  }
  if (modifiedData.budget.hasOwnProperty("budget")) {
    campaignChange.updatedItems.push(translate("label.budget"))
  }
  if (modifiedData.budget.hasOwnProperty("max_bid")) {
    campaignChange.updatedItems.push(translate("label.max.bid"))
  }
  if (modifiedData.budget.hasOwnProperty("equalizeDailyMaxSpendFromSchedule")) {
    campaignChange.updatedItems.push(translate("label.equalizeDailyMaxSpendFromSchedule"))
  }
  if (modifiedData.budget.hasOwnProperty("max_daily_spend")) {
    campaignChange.updatedItems.push(translate("label.daily.max.spend"))
  }
  // SCHEDULE
  if (modifiedData.schedule.hasOwnProperty("noSchedule")) {
    campaignChange.updatedItems.push(translate("label.no.schedule"))
  }
  if (modifiedData.schedule.hasOwnProperty("min_celsius")) {
    campaignChange.updatedItems.push(translate("label.temperature.under"))
  }
  if (modifiedData.schedule.hasOwnProperty("max_celsius")) {
    campaignChange.updatedItems.push(translate("label.temperature.above"))
  }
  // CAPS
  if (modifiedData.caps.hasOwnProperty("month")) {
    campaignChange.updatedItems.push(translate("cap.label.month"))
  }
  if (modifiedData.caps.hasOwnProperty("week")) {
    campaignChange.updatedItems.push(translate("cap.label.week"))
  }
  if (modifiedData.caps.hasOwnProperty("day")) {
    campaignChange.updatedItems.push(translate("cap.label.day"))
  }
  if (modifiedData.caps.hasOwnProperty("hour")) {
    campaignChange.updatedItems.push(translate("cap.label.hour"))
  }
  if (modifiedData.caps.hasOwnProperty("minute")) {
    campaignChange.updatedItems.push(translate("cap.label.minute"))
  }
  // DSP DEAL
  if (form.context.isDspDeal) {
    if (modifiedData.dspDeal.hasOwnProperty("dealid")) {
      campaignChange.updatedItems.push("Dealid")
    }
    if (modifiedData.dspDeal.hasOwnProperty("description")) {
      campaignChange.updatedItems.push(translate("label.description"))
    }
    if (modifiedData.dspDeal.hasOwnProperty("dsp_id")) {
      campaignChange.updatedItems.push(translate("dsp.title"))
    }
    if (modifiedData.dspDeal.hasOwnProperty("bid_floor")) {
      campaignChange.updatedItems.push(translate("label.bid-floor"))
    }
    if (modifiedData.dspDeal.hasOwnProperty("auction_type")) {
      campaignChange.updatedItems.push(translate("label.auction"))
    }
    if (modifiedData.dspDeal.hasOwnProperty("advertiserDomainIds")) {
      campaignChange.updatedItems.push(translate("label.advertiser-domain"))
    }
    if (modifiedData.dspDeal.hasOwnProperty("formatIds")) {
      campaignChange.updatedItems.push(translate("label.format"))
    }
    if (modifiedData.dspDeal.hasOwnProperty("seatIds")) {
      campaignChange.updatedItems.push(translate("label.dsp-seat"))
    }
  }
  const changes: IFormChange[] = [finalizeChange(campaignChange)]
  // SCHEDULE ITEMS
  if (modifiedData.schedule.hasOwnProperty("scheduleItems") && form.context.initialData) {
    if (!modifiedData.schedule.noSchedule) {
      changes.push(
        getFinalizedArrayChange(
          "campaign.configuration.schedule.title",
          form.context.initialData.configuration.schedule.scheduleItems
            ? form.context.initialData.configuration.schedule.scheduleItems.map(i => `${i.start}-${i.end}`)
            : undefined,
          modifiedData.schedule.scheduleItems
            ? modifiedData.schedule.scheduleItems.map(i => `${i.start}-${i.end}`)
            : undefined,
          id => id as string
        )
      )
    }
  }
  // AUCTIONS
  if (modifiedData.budget.hasOwnProperty("auctions") && form.context.initialData) {
    changes.push(
      getFinalizedArrayChange(
        "label.auction",
        form.context.initialData.configuration.budget.auctions,
        modifiedData.budget.auctions,
        id => id as string
      )
    )
  }
  // WEATHER
  if (modifiedData.schedule.hasOwnProperty("weathers") && form.context.initialData) {
    changes.push(
      getFinalizedArrayChange(
        "label.preferred.weather",
        form.context.initialData.configuration.schedule.weathers,
        modifiedData.schedule.weathers,
        id => id as string
      )
    )
  }
  // TAGS
  if (modifiedData.main.hasOwnProperty("userTagIds") && form.context.initialData) {
    changes.push(
      getFinalizedArrayChange(
        "label.tag.title",
        form.context.initialData.configuration.main.userTagIds,
        modifiedData.main.userTagIds,
        id => id as string
      )
    )
  }
  return changes
}

export function getModifiedDataSinceLastUpdate(form: IForm, useInitialData?: boolean): IFormConfigurationModifiedData {
  const modifiedData: IFormConfigurationModifiedData = {
    main: {},
    budget: {},
    caps: {},
    schedule: {},
    dspDeal: {}
  }
  const previousData = useInitialData ? form.context.initialData : form.context.lastSavedData
  if (previousData === null) {
    // MAIN
    if (form.configuration.main.name !== "") {
      modifiedData.main.name = form.configuration.main.name
    }
    if (form.configuration.main.description) {
      modifiedData.main.description = form.configuration.main.description
    }
    if (form.configuration.main.order_id !== null) {
      modifiedData.main.order_id = form.configuration.main.order_id
    }
    if (form.configuration.main.advertiser_id !== null) {
      modifiedData.main.advertiser_id = form.configuration.main.advertiser_id
    }
    if (form.configuration.main.language_id !== DEFAULT_CAMPAIGN_LANGUAGE) {
      modifiedData.main.language_id = form.configuration.main.language_id
    }
    if (form.configuration.main.startDate !== null) {
      modifiedData.main.startDate = form.configuration.main.startDate
    }
    if (form.configuration.main.endDate !== null) {
      modifiedData.main.endDate = form.configuration.main.endDate
    }
    if (form.configuration.main.unlimitedImpressions !== true) {
      modifiedData.main.unlimitedImpressions = form.configuration.main.unlimitedImpressions
      if (!modifiedData.main.unlimitedImpressions && form.configuration.main.max_views !== 0) {
        modifiedData.main.max_views = form.configuration.main.max_views
      }
    }
    if (form.configuration.main.userTagIds.length > 0) {
      modifiedData.main.userTagIds = form.configuration.main.userTagIds
    }
    if (form.configuration.main.isBiddingOnlyOnDeals !== false) {
      modifiedData.main.isBiddingOnlyOnDeals = true
    }
    // BUDGET
    if (form.configuration.budget.currency_id !== DEFAULT_FORM_CURRENCY) {
      modifiedData.budget.currency_id = form.configuration.budget.currency_id
    }
    if (form.configuration.budget.budget !== null) {
      modifiedData.budget.budget = form.configuration.budget.budget
    }
    if (form.configuration.budget.max_bid !== null) {
      modifiedData.budget.max_bid = form.configuration.budget.max_bid
    }
    if (form.configuration.budget.auctions.length > 0) {
      modifiedData.budget.auctions = form.configuration.budget.auctions
    }
    if (!form.configuration.budget.equalizeDailyMaxSpendFromSchedule) {
      modifiedData.budget.equalizeDailyMaxSpendFromSchedule = false
    }
    if (form.configuration.budget.max_daily_spend !== null) {
      modifiedData.budget.max_daily_spend = form.configuration.budget.max_daily_spend
    }
    // SCHEDULE
    if (form.configuration.schedule.noSchedule === false) {
      modifiedData.schedule.noSchedule = false
      if (form.configuration.schedule.scheduleItems.length > 0) {
        modifiedData.schedule.scheduleItems = form.configuration.schedule.scheduleItems
      }
    }
    if (form.configuration.schedule.min_celsius !== null) {
      modifiedData.schedule.min_celsius = form.configuration.schedule.min_celsius
    }
    if (form.configuration.schedule.max_celsius !== null) {
      modifiedData.schedule.max_celsius = form.configuration.schedule.max_celsius
    }
    if (form.configuration.schedule.weathers.length !== 0) {
      modifiedData.schedule.weathers = form.configuration.schedule.weathers
    }
    // CAPS
    if (form.configuration.caps.month !== null) {
      modifiedData.caps.month = form.configuration.caps.month
    }
    if (form.configuration.caps.week !== null) {
      modifiedData.caps.week = form.configuration.caps.week
    }
    if (form.configuration.caps.day !== null) {
      modifiedData.caps.day = form.configuration.caps.day
    }
    if (form.configuration.caps.hour !== null) {
      modifiedData.caps.hour = form.configuration.caps.hour
    }
    if (form.configuration.caps.minute !== null) {
      modifiedData.caps.minute = form.configuration.caps.minute
    }
    // DSP DEAL
    if (form.configuration.dspDeal.dsp_id !== null) {
      modifiedData.dspDeal.dsp_id = form.configuration.dspDeal.dsp_id
    }
    if (form.configuration.dspDeal.auction_type !== DEFAULT_DSP_DEAL_AUCTION_TYPE) {
      modifiedData.dspDeal.auction_type = form.configuration.dspDeal.auction_type
    }
    if (form.configuration.dspDeal.bid_floor !== 0) {
      modifiedData.dspDeal.bid_floor = form.configuration.dspDeal.bid_floor
    }
    if (form.configuration.dspDeal.dealid !== null) {
      modifiedData.dspDeal.dealid = form.configuration.dspDeal.dealid
    }
    if (form.configuration.dspDeal.description !== null && form.configuration.dspDeal.description !== "") {
      modifiedData.dspDeal.description = form.configuration.dspDeal.description
    }
    if (form.configuration.dspDeal.formatIds.length > 0) {
      modifiedData.dspDeal.formatIds = form.configuration.dspDeal.formatIds
    }
    if (form.configuration.dspDeal.advertiserDomainIds.length > 0) {
      modifiedData.dspDeal.advertiserDomainIds = form.configuration.dspDeal.advertiserDomainIds
    }
    return modifiedData
  }
  const mainKeys: Array<keyof IFormConfigurationMain> = Object.keys(form.configuration.main) as any
  for (const mainKey of mainKeys) {
    if (!deepEquals(previousData.configuration.main[mainKey], form.configuration.main[mainKey])) {
      modifiedData.main[mainKey] = form.configuration.main[mainKey] as any
    }
  }
  const budgetKeys: Array<keyof IFormConfigurationBudget> = Object.keys(form.configuration.budget) as any
  for (const budgetKey of budgetKeys) {
    if (!deepEquals(previousData.configuration.budget[budgetKey], form.configuration.budget[budgetKey])) {
      modifiedData.budget[budgetKey] = form.configuration.budget[budgetKey] as any
    }
  }
  const scheduleKeys: Array<keyof IFormConfigurationSchedule> = Object.keys(form.configuration.schedule) as any
  for (const scheduleKey of scheduleKeys) {
    if (!deepEquals(previousData.configuration.schedule[scheduleKey], form.configuration.schedule[scheduleKey])) {
      modifiedData.schedule[scheduleKey] = form.configuration.schedule[scheduleKey] as any
    }
  }
  const capsKeys: Array<keyof IFormConfigurationCaps> = Object.keys(form.configuration.caps) as any
  for (const capsKey of capsKeys) {
    if (!deepEquals(previousData.configuration.caps[capsKey], form.configuration.caps[capsKey])) {
      modifiedData.caps[capsKey] = form.configuration.caps[capsKey] as any
    }
  }
  const dspDealKeys: Array<keyof IFormConfigurationDspDeal> = Object.keys(form.configuration.dspDeal) as any
  for (const dspDealKey of dspDealKeys) {
    if (!deepEquals(previousData.configuration.dspDeal[dspDealKey], form.configuration.dspDeal[dspDealKey])) {
      modifiedData.dspDeal[dspDealKey] = form.configuration.dspDeal[dspDealKey] as any
    }
  }
  return modifiedData
}

export const canChangeCampaignOrder = (statusId: CAMPAIGN_STATUSES): boolean => !isStatusRunningOrRunned(statusId)

export const isCampaignImpressionsGreaterThanOrderImpression = (
  maxImpressions: number | null,
  { max_impressions }: IOrder
): boolean => {
  if (!maxImpressions || !max_impressions) {
    return false
  }
  return maxImpressions > max_impressions
}

export const isCampaignBudgetGreaterThanOrderBudget = (
  maxBudget: number | null,
  maxBudgetCurrency: CURRENCIES | null,
  { budget, budget_currency_id }: IOrder
): boolean => {
  if (!maxBudget || !budget || !maxBudgetCurrency || !budget_currency_id) {
    return false
  }
  if (maxBudgetCurrency !== budget_currency_id) {
    maxBudget = convertValueIntoCurrency(maxBudgetCurrency, budget_currency_id as CURRENCIES, maxBudget)
  }
  return maxBudget > budget
}

export async function processCreateOrUpdateCampaign(form: IForm): Promise<void> {
  let tempCampaignId = form.tempCampaignId
  try {
    let campaign: ICampaign | undefined = undefined
    let dspDeal: IDspDeal | undefined = undefined
    const isForecastForm = form.context.isForecast
    const isDspDealForm = form.context.isDspDeal
    const isCreation = form.tempCampaignId === NOT_SAVED_ID
    if (isCreation) {
      campaign = await campaignRPO.create({
        product: form.configuration.main.name as string,
        description: form.configuration.main.description ? form.configuration.main.description : undefined,
        inventory_type_id: "dooh",
        order_id: isForecastForm ? getForecastOrder().id : (form.configuration.main.order_id as number),
        advertiser_id: isForecastForm || isDspDealForm ? undefined : (form.configuration.main.advertiser_id as number),
        type: isDspDealForm ? "Deal" : "RTB",
        targeting_level_id: "ron",
        status_id: form.statusId as any,
        currency_id: form.configuration.budget.currency_id as any,
        language_id: form.configuration.main.language_id as any,
        start_date: form.configuration.main.startDate ? form.configuration.main.startDate.toISOString() : undefined,
        end_date: form.configuration.main.endDate ? form.configuration.main.endDate.toISOString() : undefined,
        max_views: form.configuration.main.unlimitedImpressions ? 0 : form.configuration.main.max_views,
        is_bidding_only_on_deals: form.configuration.main.isBiddingOnlyOnDeals,
        representative_id: getUserAgency().representative_id
        // in creation only configuration main form is enabled and all other form values are read only
        // so here we don't need to add some other form values
      })
      if (isDspDealForm) {
        if (typeof form.configuration.dspDeal.dsp_id !== "number") {
          console.error("Missing dsp_deal")
          getStore().dispatch(formActionCreator.setSaveError(form.context.appContext, FORM_SAVE_ERRORS.INVALID_FORM))
          return
        }
        dspDeal = await createDspDeal({
          campaign_id: campaign.id,
          dsp_id: form.configuration.dspDeal.dsp_id,
          dealid: form.configuration.dspDeal.dealid || undefined,
          description: form.configuration.dspDeal.description || undefined,
          bid_floor: form.configuration.dspDeal.bid_floor
        })
        form.configuration.dspDeal = {
          ...form.configuration.dspDeal,
          ...dspDeal,
          bid_floor: dspDeal.bid_floor || 0
        }
      }
      // setForm will stop saving in the store
      getStore().dispatch(
        formActionCreator.setForm({
          form: {
            ...form,
            tempCampaignId: campaign.id,
            tempDspDealId: dspDeal ? dspDeal.id : NOT_SAVED_ID
          },
          lastSavedData: getFormData(form),
          tempCampaignId: campaign.id,
          tempDspDealId: dspDeal ? dspDeal.id : undefined
        })
      )
      await addAllSspToNewCampaign(campaign.id, isForecastForm, isDspDealForm)
      await addAuctionsToNewCampaign(campaign.id, isForecastForm, isDspDealForm)
    } else {
      const modifiedData = getModifiedDataSinceLastUpdate(form)
      if (
        !isEmptyObject(modifiedData.main) ||
        !isEmptyObject(modifiedData.budget) ||
        !isEmptyObject(modifiedData.caps) ||
        !isEmptyObject(modifiedData.schedule)
      ) {
        campaign = await campaignRPO.update(getConfigurationUpdateParams(form, modifiedData))
      }
      if (!isEmptyObject(modifiedData.dspDeal)) {
        const keys = Object.keys(modifiedData.dspDeal)
        if (!(keys.indexOf("advertiserDomainIds") !== -1 && keys.length === 1)) {
          await updateDspDeal(getConfigurationDspDealUpdateParams(form, modifiedData))
        }
      }
      await updateConfigurationRelations(form, modifiedData)
      getStore().dispatch(
        formActionCreator.stopSavingTempCampaign(
          form.context.appContext,
          getConfigurationChanges(form),
          getFormData(form)
        )
      )
    }
    if (!isDspDealForm) {
      refreshInventory({ campaignName: campaign ? campaign.product || "" : "", ignoreWarnMessage: isCreation })
    }
  } catch (error) {
    console.error("An error occured while saving the form", error)
    getStore().dispatch(formActionCreator.setSaveError(form.context.appContext, FORM_SAVE_ERRORS.SERVER))
  }
  if (state.pendingFormToSave) {
    getStore().dispatch(formActionCreator.startSavingTempCampaign(form.context.appContext))
    let pendingForm = state.pendingFormToSave
    state.pendingFormToSave = null
    if (pendingForm.tempCampaignId === NOT_SAVED_ID) {
      pendingForm = {
        ...form,
        ...pendingForm
      }
      pendingForm.tempCampaignId = tempCampaignId
    }
    processCreateOrUpdateCampaign(pendingForm)
  }
}

function validateConfigurationMain(
  isForecast: boolean,
  isDspDeal: boolean,
  newData: IFormConfigurationMain
): FormikErrors<IFormConfigurationMain> {
  const errors: FormikErrors<IFormConfigurationMain> = validateDuplicateFormData(isForecast, isDspDeal, newData)
  if (typeof newData.name !== "string" || !newData.name) {
    errors.name = translate("label.field.required")
  } else if (newData.name.length > MAX_FIELD_LENGTH) {
    errors.name = translate("label.field.max.length", [MAX_FIELD_LENGTH, newData.name.length])
  }
  if (isForecast) {
    if (newData.description !== null) {
      if (typeof newData.description !== "string") {
        errors.description = translate("label.field.invalid.value")
      } else if (newData.description.length > MAX_FIELD_LENGTH) {
        errors.description = translate("label.field.max.length", [MAX_FIELD_LENGTH, newData.description.length])
      }
    }
  }
  if (typeof newData.unlimitedImpressions !== "boolean") {
    errors.unlimitedImpressions = translate("label.field.required")
  }
  if (typeof newData.max_views !== "number") {
    if (newData.unlimitedImpressions !== true) {
      errors.max_views = translate("label.field.required")
    }
  } else if (!Number.isInteger(newData.max_views)) {
    errors.max_views = translate("label.field.integer")
  } else if (newData.max_views < 0 && !newData.unlimitedImpressions) {
    errors.max_views = translate("label.field.greater.than.or.equals", 0)
  }
  return errors
}

function validateConfigurationBudget(
  isDspDeal: boolean,
  formConfiguration: IFormConfiguration,
  newData: IFormConfigurationBudget
): FormikErrors<IFormConfigurationBudget> {
  const errors: FormikErrors<IFormConfigurationBudget> = {}
  let isValidBudget = false
  if (newData.budget !== null && !isDspDeal) {
    const budgetError = getBudgetError(newData.budget)
    if (budgetError) {
      errors.budget = budgetError
    } else {
      isValidBudget = true
    }
  }
  if (!newData.currency_id) {
    errors.currency_id = translate("label.field.required")
  } else if (!getAvailableCurrencyIds().includes(newData.currency_id)) {
    errors.currency_id = translate("label.field.invalid.value")
  }
  if (!isDspDeal) {
    if (typeof newData.equalizeDailyMaxSpendFromSchedule !== "boolean") {
      errors.equalizeDailyMaxSpendFromSchedule = translate("label.field.invalid.value")
    }
    if (newData.max_daily_spend !== null) {
      let maxDailySpendError = getMaxDailySpendError(newData.max_daily_spend)
      if (maxDailySpendError) {
        errors.max_daily_spend = maxDailySpendError
      } else if (
        isValidBudget &&
        formConfiguration.main.startDate &&
        formConfiguration.main.endDate &&
        formConfiguration.main.endDate >= formConfiguration.main.startDate
      ) {
        maxDailySpendError = getMaxDailySpendErrorOverCampaignInterval(
          formConfiguration.main.startDate,
          formConfiguration.main.endDate,
          newData.budget as number,
          newData.max_daily_spend
        )
        if (maxDailySpendError) {
          errors.budget = maxDailySpendError
        }
      }
    }
  }
  if (newData.max_bid !== null) {
    if (typeof newData.max_bid !== "number") {
      errors.max_bid = translate("label.field.invalid.value")
    } else if (newData.max_bid < 1) {
      errors.max_bid = translate("label.field.greater.than.or.equals", 1)
    }
  }
  if (!isDspDeal) {
    if (!Array.isArray(newData.auctions)) {
      errors.auctions = translate("label.field.invalid.value")
    } else {
      if (newData.auctions.length === 0) {
        errors.auctions = translate("label.field.required")
      } else {
        for (const auction of newData.auctions) {
          if (!(isDspDeal ? SSP_DEAL_AUCTION_TYPES : CAMPAIGN_AUCTION_TYPES).includes(auction)) {
            errors.auctions = translate("label.field.invalid.value")
          }
        }
      }
    }
  }
  return errors
}

function validateConfigurationSchedule(
  newMainData: IFormConfigurationMain,
  newData: IFormConfigurationSchedule
): FormikErrors<IFormConfigurationSchedule> {
  const errors: FormikErrors<IFormConfigurationSchedule> = {}
  if (newData.min_celsius !== null && typeof newData.min_celsius !== "number") {
    errors.min_celsius = translate("label.field.invalid.value")
  }
  if (newData.max_celsius !== null && typeof newData.max_celsius !== "number") {
    errors.max_celsius = translate("label.field.invalid.value")
  }
  if (typeof newData.noSchedule !== "boolean") {
    errors.noSchedule = translate("label.field.invalid.value")
  }
  if (!Array.isArray(newData.scheduleItems)) {
    errors.scheduleItems = translate("label.field.invalid.value")
  }
  if (!Array.isArray(newData.weathers)) {
    errors.weathers = translate("label.field.invalid.value")
  } else {
    for (const weather of newData.weathers) {
      if (!ALL_WEATHERS.includes(weather)) {
        errors.weathers = translate("label.field.invalid.value")
        break
      }
    }
  }
  if (!newData.noSchedule && newData.scheduleItems) {
    const startDate = newMainData.startDate
    const endDate = newMainData.endDate
    if (startDate && endDate && !(startDate > endDate)) {
      const weekdays = getAllWeekDaysOfPeriod(startDate, endDate)
      if (weekdays.length < 7) {
        for (const scheduleItem of newData.scheduleItems) {
          if (!weekdays.includes(scheduleItem.start.getDay())) {
            errors.scheduleItems = translate("label.field.invalid.schedule")
            break
          }
        }
      }
    }
  }
  return errors
}

function validateConfigurationCaps(
  _formConfiguration: IFormConfiguration,
  newData: IFormConfigurationCaps
): FormikErrors<IFormConfigurationCaps> {
  const errors: FormikErrors<IFormConfigurationCaps> = {}
  const caps: Array<ICap | null> = Object.values(newData)
  for (const cap of caps) {
    if (cap === null) {
      continue
    }
    if (!isValidCap(cap)) {
      errors[cap.period] = translate("label.field.invalid.value")
    } else if (cap.frequency < 1) {
      errors[cap.period] = translate("label.field.greater.than.or.equals", 1)
    } else if (!Number.isInteger(cap.frequency)) {
      errors[cap.period] = translate("label.field.integer")
    } else if (cap.frequency > 300) {
      errors[cap.period] = translate("label.field.max.length", [300, cap.frequency])
    }
  }
  return {
    ...getCapsInterdependentErrors(newData),
    ...errors
  }
}

const validateConfigurationDspDeal = (
  _formConfiguration: IFormConfiguration,
  newData: IFormConfigurationDspDeal
): FormikErrors<IFormConfigurationDspDeal> => {
  const errors: FormikErrors<IFormConfigurationDspDeal> = {}
  if (newData.dsp_id === null) {
    errors.dsp_id = translate("label.field.required")
  }
  if (!newData.dealid) {
    errors.dealid = translate("label.field.required")
  }
  if (typeof newData.bid_floor !== "number") {
    errors.bid_floor = translate("label.field.required")
  } else if (newData.bid_floor < 0) {
    errors.bid_floor = translate("label.field.greater.than.or.equals", 0)
  }
  if (!ALL_AUCTION_TYPES.includes(newData.auction_type)) {
    errors.auction_type = translate("label.field.invalid.value")
  }
  if (newData.description !== null) {
    if (typeof newData.description !== "string") {
      errors.description = translate("label.field.invalid.value")
    } else if (newData.description.length > MAX_FIELD_LENGTH) {
      errors.description = translate("label.field.max.length", [MAX_FIELD_LENGTH, newData.description.length])
    }
  }
  return errors
}

function getBudgetError(budget: number | null): string | undefined {
  if (typeof budget !== "number") {
    return translate("label.field.invalid.value")
  } else if (!Number.isInteger(budget)) {
    return translate("label.field.integer")
  } else if (budget < 1) {
    return translate("label.field.greater.than.or.equals", 1)
  }
}

function getMaxDailySpendError(max_daily_spend: number | null): string | undefined {
  if (typeof max_daily_spend !== "number") {
    return translate("label.field.invalid.value")
  } else if (!Number.isInteger(max_daily_spend)) {
    return translate("label.field.integer")
  } else if (max_daily_spend < 1) {
    return translate("label.field.greater.than.or.equals", 1)
  }
}

function getMaxDailySpendErrorOverCampaignInterval(
  startDate: Date,
  endDate: Date,
  budget: number,
  max_daily_spend: number
): string | undefined {
  const differenceInDays = Math.ceil(getDifferenceInDay(startDate, endDate) + 1)
  const max_daily_spent_amount = max_daily_spend * differenceInDays
  if (max_daily_spent_amount > (budget as number)) {
    return translate("label.field.max-daily-spend.greater.budget", max_daily_spent_amount.toFixed(0))
  }
}
